import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Loader2, ShieldCheck, AlertTriangle, DollarSign, Scale, CheckCircle2,
  Briefcase, UserCheck, Coins, Lock, Printer,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const usDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${m}/${d}/${y}`;
};
const mondayOf = (iso) => {
  if (!iso) return iso;
  const d = new Date(iso + "T00:00:00");
  const day = d.getDay(); // 0=Sun, 1=Mon...
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
};

export default function PayrollBreakdown({ initialWeek }) {
  const [weekStart, setWeekStart] = useState(initialWeek || mondayOf(new Date().toISOString().slice(0, 10)));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (ws) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/payroll/full-breakdown/${ws}`);
      setData(r.data);
    } catch (err) {
      toast.error("Breakdown failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(weekStart); }, [weekStart]);

  if (loading || !data) {
    return <div className="bg-white rounded-lg p-12 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /> Building breakdown…</div>;
  }

  const overlapBad = !data.verification?.no_overlap;
  const cashOnSchedBad = (data.verification?.cash_employees_on_schedule || 0) > 0;
  const allClean = !overlapBad && !cashOnSchedBad;

  return (
    <div className="space-y-4 print:space-y-2">
      {/* Header */}
      <div className="bg-white rounded-lg p-4 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
          <h2 className="font-bold text-lg flex items-center gap-2">
            <Scale className="w-5 h-5 text-[#A61B1B]" /> Full Weekly Salary Breakdown
          </h2>
          <Button variant="outline" size="sm" onClick={() => window.print()} className="print:hidden" data-testid="breakdown-print">
            <Printer className="w-4 h-4 mr-1" /> Print
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-3 print:hidden">
          <span className="text-sm font-semibold text-stone-700">Week:</span>
          <Input type="date" value={weekStart} onChange={(e) => setWeekStart(mondayOf(e.target.value))} className="w-44" data-testid="breakdown-week" />
          <span className="text-sm text-stone-500">{usDate(data.week_start)} → {usDate(data.week_end)}</span>
        </div>
      </div>

      {/* Isolation Promise + Verification banner */}
      <div className={`rounded-lg p-4 border-2 ${allClean ? "bg-emerald-50 border-emerald-300" : "bg-rose-50 border-rose-300"}`}>
        <div className="flex items-start gap-3">
          {allClean ? <ShieldCheck className="w-6 h-6 text-emerald-700 mt-0.5 shrink-0" /> : <AlertTriangle className="w-6 h-6 text-rose-700 mt-0.5 shrink-0" />}
          <div>
            <h3 className={`font-bold text-base ${allClean ? "text-emerald-900" : "text-rose-900"}`}>
              {allClean ? "✓ Isolation Verified — No Mixing Detected" : "⚠ Mixing Detected — see warnings below"}
            </h3>
            <p className={`text-sm mt-0.5 ${allClean ? "text-emerald-800" : "text-rose-800"}`}>{data.isolation_promise}</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3 text-xs">
              <Check ok={!overlapBad} label="No employee in 2+ buckets" />
              <Check ok={!cashOnSchedBad} label={`Cash employees on schedule: ${data.verification.cash_employees_on_schedule}`} />
              <Check ok={true} label="Cash hours sourced from Kiosk only" />
            </div>
          </div>
        </div>
      </div>

      {/* GRAND TOTALS SUMMARY */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <BucketCard
          color="#1E40AF"
          icon={Briefcase}
          title="W-2 Employees"
          subtitle="Schedule · Full tax withholding"
          headcount={data.w2.headcount}
          gross={data.w2.totals.gross}
          extra={`Net ${fmt(data.w2.totals.net)} after $${(data.w2.totals.fed_wh + data.w2.totals.il_wh + data.w2.totals.fica_ss + data.w2.totals.fica_medicare).toFixed(2)} withheld`}
        />
        <BucketCard
          color="#7C3AED"
          icon={UserCheck}
          title="1099 Contractors"
          subtitle="Schedule · No withholding"
          headcount={data.contractors_1099.headcount}
          gross={data.contractors_1099.totals.gross}
          extra={`Gross = Net (contractors handle their own taxes)`}
        />
        <BucketCard
          color="#92400E"
          icon={Coins}
          title="Cash (Off-Books)"
          subtitle="Kiosk punches · Owner's till"
          headcount={data.cash.headcount}
          gross={data.cash.total_cash_paid_estimate}
          extra={`${data.cash.total_hours.toFixed(1)} hrs from Kiosk · NOT in W-2/1099`}
          warn
        />
      </div>

      {/* W-2 DETAIL */}
      <Section title={`W-2 Employees (${data.w2.headcount})`} icon={Briefcase} color="#1E40AF" source={data.w2.source}>
        {data.w2.rows.length === 0 ? (
          <Empty msg="No W-2 employees with hours this week." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-blue-50 text-blue-900">
                <tr>
                  <th className="text-left px-2 py-1.5">Employee</th>
                  <th className="text-left px-2 py-1.5">Dept</th>
                  <th className="text-right px-2 py-1.5">Hours</th>
                  <th className="text-right px-2 py-1.5">Rate</th>
                  <th className="text-right px-2 py-1.5">Gross</th>
                  <th className="text-right px-2 py-1.5">Fed</th>
                  <th className="text-right px-2 py-1.5">IL</th>
                  <th className="text-right px-2 py-1.5">FICA</th>
                  <th className="text-right px-2 py-1.5 font-bold">Net</th>
                </tr>
              </thead>
              <tbody>
                {data.w2.rows.map(r => (
                  <tr key={r.employee_code} className="border-b border-stone-100">
                    <td className="px-2 py-1.5 font-medium">{r.name} <span className="text-stone-400">#{r.employee_code}</span></td>
                    <td className="px-2 py-1.5 text-stone-600">{r.department}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{(r.hours_regular + r.hours_overtime).toFixed(1)}{r.hours_overtime > 0 && <sup className="text-amber-700 ml-0.5">+{r.hours_overtime.toFixed(1)}OT</sup>}</td>
                    <td className="px-2 py-1.5 text-right font-mono">${r.hourly_rate}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{fmt(r.gross_pay)}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-stone-500">-{fmt(r.fed_withholding)}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-stone-500">-{fmt(r.il_withholding)}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-stone-500">-{fmt(r.fica_ss + r.fica_medicare)}</td>
                    <td className="px-2 py-1.5 text-right font-mono font-bold text-emerald-800">{fmt(r.net_pay)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-blue-50 font-bold">
                <tr>
                  <td colSpan={4} className="px-2 py-1.5 text-right">TOTAL ({data.w2.headcount}):</td>
                  <td className="px-2 py-1.5 text-right font-mono">{fmt(data.w2.totals.gross)}</td>
                  <td className="px-2 py-1.5 text-right font-mono">-{fmt(data.w2.totals.fed_wh)}</td>
                  <td className="px-2 py-1.5 text-right font-mono">-{fmt(data.w2.totals.il_wh)}</td>
                  <td className="px-2 py-1.5 text-right font-mono">-{fmt(data.w2.totals.fica_ss + data.w2.totals.fica_medicare)}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-emerald-800">{fmt(data.w2.totals.net)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Section>

      {/* 1099 DETAIL */}
      <Section title={`1099 Contractors (${data.contractors_1099.headcount})`} icon={UserCheck} color="#7C3AED" source={data.contractors_1099.source}>
        {data.contractors_1099.rows.length === 0 ? (
          <Empty msg="No 1099 contractors with hours this week." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-purple-50 text-purple-900">
                <tr>
                  <th className="text-left px-2 py-1.5">Contractor</th>
                  <th className="text-left px-2 py-1.5">Dept</th>
                  <th className="text-right px-2 py-1.5">Hours</th>
                  <th className="text-right px-2 py-1.5">Rate</th>
                  <th className="text-right px-2 py-1.5 font-bold">Gross = Net</th>
                  <th className="text-right px-2 py-1.5">YTD</th>
                  <th className="text-center px-2 py-1.5">1099-NEC?</th>
                </tr>
              </thead>
              <tbody>
                {data.contractors_1099.rows.map(r => (
                  <tr key={r.employee_code} className="border-b border-stone-100">
                    <td className="px-2 py-1.5 font-medium">{r.name} <span className="text-stone-400">#{r.employee_code}</span></td>
                    <td className="px-2 py-1.5 text-stone-600">{r.department}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{r.hours.toFixed(1)}</td>
                    <td className="px-2 py-1.5 text-right font-mono">${r.hourly_rate}</td>
                    <td className="px-2 py-1.5 text-right font-mono font-bold">{fmt(r.gross_pay)}</td>
                    <td className="px-2 py-1.5 text-right font-mono text-stone-600">{fmt(r.ytd_plus_this_run)}</td>
                    <td className="px-2 py-1.5 text-center">{r.needs_1099_nec ? <span className="text-xs bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded font-bold">YES</span> : <span className="text-stone-300 text-xs">no</span>}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-purple-50 font-bold">
                <tr>
                  <td colSpan={4} className="px-2 py-1.5 text-right">TOTAL ({data.contractors_1099.headcount}):</td>
                  <td className="px-2 py-1.5 text-right font-mono">{fmt(data.contractors_1099.totals.gross)}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Section>

      {/* CASH DETAIL — visually isolated */}
      <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <Coins className="w-5 h-5 text-amber-800" />
          <h3 className="font-bold text-amber-900">Cash Employees (Off-the-Books) — {data.cash.headcount}</h3>
          <Lock className="w-4 h-4 text-amber-700 ml-auto" />
        </div>
        <p className="text-xs text-amber-900 italic mb-2">{data.cash.note}</p>
        <p className="text-xs text-amber-700 mb-3">Source: <strong>{data.cash.source}</strong></p>

        {data.cash.rows.length === 0 ? (
          <Empty msg="No cash employees in DB." />
        ) : (
          <div className="overflow-x-auto bg-white rounded">
            <table className="w-full text-xs">
              <thead className="bg-amber-100 text-amber-900">
                <tr>
                  <th className="text-left px-2 py-1.5">Employee</th>
                  <th className="text-left px-2 py-1.5">Dept</th>
                  <th className="text-right px-2 py-1.5">Hours (Kiosk)</th>
                  <th className="text-right px-2 py-1.5">Days</th>
                  <th className="text-right px-2 py-1.5">Rate</th>
                  <th className="text-right px-2 py-1.5 font-bold">Cash Owed</th>
                </tr>
              </thead>
              <tbody>
                {data.cash.rows.map(r => (
                  <tr key={r.employee_code} className="border-b border-amber-100">
                    <td className="px-2 py-1.5 font-medium">{r.name} <span className="text-stone-400">#{r.employee_code}</span></td>
                    <td className="px-2 py-1.5 text-stone-600">{r.department}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{r.hours_punched.toFixed(1)}</td>
                    <td className="px-2 py-1.5 text-right">{r.days_worked}</td>
                    <td className="px-2 py-1.5 text-right font-mono">${r.hourly_rate}</td>
                    <td className="px-2 py-1.5 text-right font-mono font-bold text-amber-900">{fmt(r.cash_paid_estimate)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-amber-100 font-bold">
                <tr>
                  <td colSpan={2} className="px-2 py-1.5 text-right">TOTAL:</td>
                  <td className="px-2 py-1.5 text-right font-mono">{data.cash.total_hours.toFixed(1)}h</td>
                  <td colSpan={2}></td>
                  <td className="px-2 py-1.5 text-right font-mono text-amber-900">{fmt(data.cash.total_cash_paid_estimate)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
        <p className="text-[10px] text-amber-700 mt-2 italic">
          🔒 These figures NEVER flow into the W-2 or 1099 totals above. They are computed from a separate database collection (kiosk punches) and shown here for the owner&apos;s personal records only.
        </p>
      </div>
    </div>
  );
}

const Check = ({ ok, label }) => (
  <div className={`flex items-center gap-1 ${ok ? "text-emerald-700" : "text-rose-700 font-bold"}`}>
    {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
    <span>{label}</span>
  </div>
);

const BucketCard = ({ color, icon: Icon, title, subtitle, headcount, gross, extra, warn }) => (
  <div className={`rounded-lg p-4 border-2 ${warn ? "bg-amber-50 border-amber-300" : "bg-white border-stone-200"}`}>
    <div className="flex items-center gap-2 mb-2">
      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: color + "22" }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div>
        <h4 className="font-bold text-sm">{title}</h4>
        <p className="text-[10px] text-stone-500 uppercase tracking-wider">{subtitle}</p>
      </div>
    </div>
    <div className="text-2xl font-extrabold" style={{ color }}>{fmt(gross)}</div>
    <p className="text-xs text-stone-600 mt-0.5">{headcount} employees</p>
    {extra && <p className="text-xs text-stone-500 mt-1.5 leading-tight">{extra}</p>}
  </div>
);

const Section = ({ title, icon: Icon, color, source, children }) => (
  <div className="bg-white rounded-lg shadow-sm p-4">
    <div className="flex items-center justify-between mb-2">
      <h3 className="font-bold text-sm flex items-center gap-2"><Icon className="w-4 h-4" style={{ color }} /> {title}</h3>
      <span className="text-[10px] text-stone-400 uppercase tracking-wider">Source: {source}</span>
    </div>
    {children}
  </div>
);

const Empty = ({ msg }) => <p className="text-sm text-stone-500 italic text-center py-4">{msg}</p>;
